import {
  EMPTY_TECHNICIAN_BACKGROUND,
  TechnicianBackgroundFields,
  hasTechnicianBackground,
  type TechnicianBackgroundValue,
} from '@/components/technicians/TechnicianBackgroundFields';
import { setTechnicianTradeBackground } from '@/hooks/technicians/useTechnicianBackground';
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { consumeFirstUse, issueFirstUse } from '@/lib/onboarding/firstUseFlag';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { getBrowserTimezone } from '@/utils/formatShopTime';
import { CORE_PRESET } from '@/lib/features/featureFlags';
import { WorkRoleMultiSelect } from '@/components/roles/WorkRoleMultiSelect';
import type { WorkRole } from '@/lib/roles/multiRole';

const OnboardShop = () => {
  const navigate = useNavigate();
  const { user, membership, userShops, isLoading: isAuthLoading, isShopDataLoading } = useAuth();
  
  const [isLoading, setIsLoading] = useState(false);
  // Core-100 R3 CORE-U: one setup act = one key. A double submit, a second tab's retry or a retried request
  // after a lost response sends the SAME key, and the server returns the same shop instead of a second one.
  const foundingKeyRef = useRef<string>(crypto.randomUUID());
  const [shopName, setShopName] = useState('');
  const [address, setAddress] = useState('');
  // Auto-detect user's browser timezone instead of defaulting to Pacific
  const [timeZone, setTimeZone] = useState(() => getBrowserTimezone());

  // ── WHAT THIS PERSON DOES, ASKED ONCE, AT THE ONLY MOMENT IT IS FREE ──────
  //
  // Owning a shop and working in it are different facts, and the one does not
  // imply the other. Plenty of owners write repair orders; plenty do not touch
  // a vehicle. Before this, creating a shop made you a MANAGER and the system
  // simply had no representation for "…who also turns wrenches" — so the
  // question was never asked and the answer was guessed.
  //
  // The default is empty rather than TECHNICIAN. Guessing a work role here is
  // exactly the fabrication the backfill refused to make: it would put a
  // brand-new owner into the mechanical assignment pool on their first day
  // without anybody having said they belong there.
  const [workRoles, setWorkRoles] = useState<WorkRole[]>([]);
  // Roadmap #7: an owner who also turns wrenches is asked about their own
  // trade background — only when they said they do TECHNICIAN work.
  const [background, setBackground] = useState<TechnicianBackgroundValue>(EMPTY_TECHNICIAN_BACKGROUND);
  const asksBackground = workRoles.includes('TECHNICIAN' as WorkRole);

  // Redirect if not authenticated.
  //
  // Phase 3 Batch C: this effect previously had NO loading guard. `user` is
  // transiently null while AuthContext resolves the session, so a fresh load of
  // /onboard/shop — or any auth re-resolution — bounced to /auth before the
  // session arrived. /auth then sees a live session and renders its
  // "Account Management" view rather than returning the user to the app, so the
  // bounce is a DEAD END: signed in, but parked on the login route.
  //
  // That is what broke the hosted browser suites. The shop was created
  // successfully (verified in QA: row present, membership present) and the app
  // still ended up on /auth, so `waitForURL('/')` timed out.
  //
  // Waiting for auth `isLoading` to settle is the same guard the sibling effect
  // below already applies via `isShopDataLoading`, and the same one
  // ProtectedRoute uses before its own `!user` redirect. No authentication is
  // weakened: an genuinely unauthenticated visitor still lands on /auth, just
  // after auth has actually resolved.
  useEffect(() => {
    if (isAuthLoading) return;
    if (!user) {
      console.log('[OnboardShop] No user found after auth resolved, redirecting to /auth');
      navigate('/auth');
    }
  }, [user, isAuthLoading, navigate]);


  // If the user already has an active shop/membership, send them to the dashboard
  // Add slight delay to prevent redirect before AuthContext finishes loading membership
  useEffect(() => {
    if (user && !isShopDataLoading && (membership || (userShops?.length || 0) > 0)) {
      const redirectTimer = setTimeout(() => {
        console.log('[OnboardShop] User has membership, redirecting to dashboard');
        navigate('/', { replace: true });
      }, 300);
      
      return () => clearTimeout(redirectTimer);
    }
  }, [user, membership, userShops, isShopDataLoading, navigate]);

  const handleCreateShop = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) return;

    setIsLoading(true);

    try {
      // Core-100 R3 CORE-U: the founding act is ONE server transaction — shop, its organization wrapper, the
      // founding MANAGER membership and the profile commit together or not at all (create_shop_founding).
      // The browser used to issue three statements with best-effort compensating deletes; a failed rollback
      // left an orphan shop and a double submit founded two shops.
      const { data: founded, error: foundingError } = await supabase.rpc('create_shop_founding' as never, {
        p_idempotency_key: foundingKeyRef.current,
        p_name: shopName,
        p_address: address,
        p_time_zone: timeZone,
      } as never);
      if (foundingError) throw foundingError;
      const shop = { id: (founded as { shop_id: string }).shop_id };

      // ── WORK ───────────────────────────────────────────────────────────
      //
      // Through the server, never by writing the table. `membership_work_roles`
      // has RLS on and no client write policy at all, which is what makes it
      // impossible for a browser to grant itself a role; signup is not an
      // exception to that and does not get one.
      //
      // A failure here does NOT roll the shop back. The shop and the
      // membership are real and usable, and the owner can set their own roles
      // in Team Management in about four seconds. Destroying a just-created
      // shop over a secondary call would be a far worse outcome than starting
      // with an empty work-role set, which is itself a valid answer.
      // Called even when the answer is EMPTY. "I do no vehicle work" is an
      // answer, and it has to be recorded — an owner who is left looking
      // undescribed falls back to the legacy scalar in the assignment guard,
      // which is the opposite of what they just told us.
      {
        const { error: rolesError } = await supabase.functions.invoke('manage-membership-roles', {
          body: { shop_id: shop.id, target_user_id: user.id, work_roles: workRoles },
        });
        if (rolesError) {
          console.error('[OnboardShop] work roles not applied:', rolesError);
          toast({
            title: 'Shop created — work roles not saved',
            description: 'Your shop is ready. Set what you do in the shop from Team Management.',
          });
        } else if (asksBackground && hasTechnicianBackground(background)) {
          // Self-reported, on the owner's own technician row (created just
          // now by manage-membership-roles). Best-effort: it can be added or
          // corrected later from their Passport.
          try {
            await setTechnicianTradeBackground({
              shopId: shop.id, technicianId: null,
              experienceLevel: background.experienceLevel, yearsProfessional: background.yearsProfessional,
            });
          } catch (bgError) {
            console.warn('[OnboardShop] technician background not saved (non-blocking):', bgError);
          }
        }
      }

      toast({
        title: 'Shop created!',
        description: `${shopName} is ready to go.`,
      });

      // Set the newly created shop as active
      localStorage.setItem('activeShopId', shop.id);

      // Adoption Slice 1: when the new lean 3-page setup wizard is part
      // of the Core preset, route fresh shops through it instead of the
      // legacy manager tutorial autostart. The legacy path remains the
      // fallback so existing shops created before this flag was on are
      // unaffected.
      if (CORE_PRESET.firstLoginV2) {
        // Suppress the first-use invitation — CORE-W's lean setup wizard owns
        // first login for a fresh manager.
        //
        // CORE-BL DECISION (closure pass 3), recorded rather than assumed:
        // CORE-BL does NOT issue its manager walkthrough after the wizard
        // completes. The wizard PERFORMS setup — shop basics and hours,
        // people counts, team, bays, specialty and parking — across three
        // pages. The CORE-BL manager track is a 42-step walkthrough OF those
        // same configuration screens: "click Shop Profile", "General Info",
        // "Hours tab", "invite a member". Issuing it immediately afterwards
        // would walk a manager through configuring a shop that is already
        // configured, starting with data they had just entered.
        //
        // So the manager walkthrough remains available ON DEMAND (account
        // menu, help button) as reference, and is not pushed at somebody who
        // has just finished the same job a faster way. What a fresh manager
        // still lacks afterwards is day-to-day OPERATIONS instruction, and
        // CORE-BL has not authored a manager operations track — that is an
        // authored-content gap (CORE-BL-GAP-011), not something to paper over
        // by issuing a setup tour that does not fit.
        consumeFirstUse(shop.id);
        window.location.replace('/onboard/setup');
        return;
      }

      // Legacy path (firstLoginV2 disabled): there is no wizard, so the
      // manager walkthrough IS their first-use education. Issued through the
      // one helper, named with the shop it belongs to.
      issueFirstUse(shop.id);
      window.location.replace('/');
    } catch (error: any) {
      toast({
        title: 'Error creating shop',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md sm:max-w-lg">
        <CardHeader>
          <CardTitle>Create Your Shop</CardTitle>
          <CardDescription>Set up your shop to get started with OptiBay</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCreateShop} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="shop-name">Shop Name *</Label>
              <Input
                id="shop-name"
                type="text"
                placeholder="Premier Auto Service"
                value={shopName}
                onChange={(e) => setShopName(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                type="text"
                placeholder="123 Main St, City, State"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="timezone">Time Zone</Label>
              <select
                id="timezone"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={timeZone}
                onChange={(e) => setTimeZone(e.target.value)}
              >
                <option value="America/Los_Angeles">Pacific Time (PT)</option>
                <option value="America/Denver">Mountain Time (MT)</option>
                <option value="America/Chicago">Central Time (CT)</option>
                <option value="America/New_York">Eastern Time (ET)</option>
              </select>
            </div>
            {/* Separated from the fields above by a rule, because this is a
                question about the PERSON rather than about the shop. */}
            <div className="border-t pt-4">
              <WorkRoleMultiSelect
                value={workRoles}
                onChange={setWorkRoles}
                disabled={isLoading}
                label="What do you do in the shop?"
                description="You are the shop's manager either way — this is about the work you personally perform. Choose as many as apply, or none if you do not work on vehicles yourself."
                idPrefix="signup-work-role"
              />
              {asksBackground && (
                <div className="mt-4" data-testid="signup-technician-background">
                  <TechnicianBackgroundFields
                    value={background}
                    onChange={setBackground}
                    perspective="self"
                    idPrefix="signup-tech-bg"
                    disabled={isLoading}
                  />
                </div>
              )}
            </div>

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? 'Creating shop...' : 'Create Shop'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default OnboardShop;
